import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Campaign {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_hidden: boolean;
  hero_title: string | null;
  hero_subtitle: string | null;
  created_at: string;
}

export interface CampaignPackage {
  id: string;
  campaign_id: string;
  name: string;
  description: string | null;
  price: number;
  original_value: number | null;
  discount_percent: number | null;
  credits_amount: number;
  includes_nimi_plus: boolean;
  first_time_only: boolean;
  display_order: number;
  is_active: boolean;
  grants_unlimited_days: number | null;
  unlimited_start_date: string | null;
  unlimited_end_date: string | null;
  created_at: string;
  package_type: string;
  subscription_price_monthly: number | null;
  subscription_credits_monthly: number | null;
  trial_days: number | null;
  partner_code_filter: string | null;
  tier_slug: string | null;
}

export function useActiveCampaign() {
  return useQuery({
    queryKey: ["active-campaign"],
    queryFn: async () => {
      // Fetch active campaign within date range
      const { data: campaign, error: campaignError } = await supabase
        .from("campaigns")
        .select("*")
        .eq("is_active", true)
        .eq("is_hidden", false)
        .lte("start_date", new Date().toISOString())
        .gt("end_date", new Date().toISOString())
        .single();

      if (campaignError || !campaign) {
        return null;
      }

      // Fetch packages for this campaign
      const { data: packages, error: packagesError } = await supabase
        .from("campaign_packages")
        .select("*")
        .eq("campaign_id", campaign.id)
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (packagesError) {
        console.error("Error fetching packages:", packagesError);
        return null;
      }

      return {
        campaign: campaign as Campaign,
        packages: packages as CampaignPackage[],
      };
    },
  });
}

export function useHasActiveCampaign() {
  const { data, isLoading } = useActiveCampaign();
  return {
    hasActiveCampaign: !!data?.campaign,
    isLoading,
  };
}

export function useCampaignBySlug(slug: string | undefined) {
  return useQuery({
    queryKey: ["campaign-by-slug", slug],
    queryFn: async () => {
      if (!slug) return null;

      const { data: campaign, error: campaignError } = await supabase
        .from("campaigns")
        .select("*")
        .eq("slug", slug)
        .eq("is_active", true)
        .lte("start_date", new Date().toISOString())
        .gt("end_date", new Date().toISOString())
        .single();

      if (campaignError || !campaign) {
        return null;
      }

      const { data: packages, error: packagesError } = await supabase
        .from("campaign_packages")
        .select("*")
        .eq("campaign_id", campaign.id)
        .eq("is_active", true)
        .order("display_order", { ascending: true });

      if (packagesError) {
        console.error("Error fetching packages:", packagesError);
        return null;
      }

      return {
        campaign: campaign as Campaign,
        packages: packages as CampaignPackage[],
      };
    },
    enabled: !!slug,
  });
}
